import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_menu.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { Props, DivAttributes, InputAttributes } from '../utils/types'
import { consumeOverlayTrigger } from './OverlayTriggerNext'
import { useListNavigation } from '../state/ListNavigation'
import { useHotkey } from '../effects/Hotkey'
import Box, { BoxProps, FlexDirectionNS } from '../layouts/Box'
import Flex from '../layouts/Flex'
import { SearchProps, SearchView } from './Search'

type ElementTypeChecker = (
  element: any,
  index?: number,
  list?: any[]
) => boolean

export interface Selection {
  event: React.MouseEvent<HTMLElement, MouseEvent>
  group: boolean
  item: any
}

export interface NavigationProps extends Props {
  hover?: boolean
  ref?: React.Ref<any>
}

interface ItemProps extends NavigationProps {
  children?: React.ReactNode
  className?: string
  hover?: boolean
  item: any
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => any
  selected?: boolean
  suggested?: boolean
}

interface GroupProps {
  className?: string
  clickable?: boolean
  item?: any
}

interface OptionsProps {
  children?: React.ReactNode
  className?: string
  scrollable?: boolean
}

interface FooterProps extends BoxProps {}

export interface MenuProps extends OptionsProps {
  onSelect?: (selection: Selection) => any
  initialIndex?: number
  isSelectable?: ElementTypeChecker
}

export interface MenuApi {
  hoverRef: React.Ref<any>
  index: number
  isSelectable: ElementTypeChecker
  onSelect: (selection: Selection) => any
  scrollable?: boolean
  setSize: (size: number) => void
}

const cx = bind(styles)

export const isItem: ElementTypeChecker = element => element.type === Item

const preventDefault = (cb: Function) => (e: Event) => {
  e.preventDefault()
  return cb(e)
}

const scrollIntoView = (index: number, element: HTMLElement) => {
  if (!element.parentElement) {
    return
  }

  if (index === 0) {
    element.parentElement.scrollTop = 0

    return
  }

  const { offsetTop, clientHeight } = element
  const { scrollTop, clientHeight: parentHeight } = element.parentElement

  if (offsetTop < scrollTop) {
    element.parentElement.scrollTop = offsetTop
  } else if (offsetTop + clientHeight > scrollTop + parentHeight) {
    element.parentElement.scrollTop = offsetTop - parentHeight + clientHeight
  }
}

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      children,
      className,
      initialIndex = -1,
      onSelect = () => {},
      scrollable = true,
      isSelectable = isItem,
      ...props
    },
    ref
  ) => {
    const menuRef =
      (ref as React.RefObject<HTMLDivElement>) ||
      React.createRef<HTMLDivElement>()

    const { overlay } = consumeOverlayTrigger()

    const select = React.useCallback(selection => {
      onSelect && onSelect(selection)
      overlay.hide(selection.event)
    }, [])

    const [size, setSize] = React.useState(0)

    const listNavigation = useListNavigation({
      circular: true,
      initialIndex,
      size,
    })

    const hoverRef = React.createRef<HTMLDivElement>()

    const selectCurrent = () => hoverRef.current && hoverRef.current.click()

    useHotkey({
      key: 'Enter',
      handler: selectCurrent,
      scope: menuRef,
    })

    useHotkey({
      key: ['ArrowDown', 'Down'],
      handler: preventDefault(listNavigation.increment),
      scope: menuRef,
    })

    useHotkey({
      key: ['ArrowUp', 'Up'],
      handler: preventDefault(listNavigation.decrement),
      scope: menuRef,
    })

    return (
      <MenuContext.Provider
        value={{
          index: listNavigation.index,
          onSelect: select,
          scrollable,
          hoverRef,
          isSelectable,
          setSize,
        }}
      >
        <div className={cx('core-menu-wrapper')}>
          <Flex
            ref={menuRef}
            tabIndex={0}
            direction={FlexDirectionNS.column}
            className={cx('core-menu', className)}
            {...props}
          >
            {children}
          </Flex>
        </div>
      </MenuContext.Provider>
    )
  }
)

export const Group = React.forwardRef<
  HTMLDivElement,
  DivAttributes & GroupProps
>(({ children, className, clickable = false, item, ...props }, ref) => {
  const { onSelect } = consumeMenu()

  return (
    <div
      ref={ref}
      className={cx('core-menu__group', className, {
        [`core-menu__group--clickable`]: clickable,
      })}
      onClick={event => clickable && onSelect({ event, item, group: true })}
      {...props}
    >
      <Box padding="xs lg">{children}</Box>
    </div>
  )
})

export const Item = React.forwardRef<HTMLDivElement, DivAttributes & ItemProps>(
  (
    {
      children,
      className,
      hover = false,
      item,
      onClick = () => {},
      selected = false,
      suggested,
      ...props
    },
    ref
  ) => {
    const { onSelect, scrollable, index } = consumeMenu()

    const itemRef =
      (ref as React.RefObject<HTMLDivElement>) ||
      React.createRef<HTMLDivElement>()

    React.useEffect(() => {
      if (itemRef.current && scrollable && (hover || selected)) {
        scrollIntoView(index, itemRef.current)
      }
    }, [hover])

    return (
      <div
        className={cx('core-menu__item', className, {
          [`core-menu__item--hover`]: hover,
          [`core-menu__item--selected`]: selected,
        })}
        onClick={event => {
          onClick && onClick(event)
          onSelect({ event, item, group: false })
        }}
        ref={itemRef}
        {...props}
      >
        {children}
      </div>
    )
  }
)

export const CSSMenuOptions = React.forwardRef<
  HTMLDivElement,
  DivAttributes & OptionsProps & { padding?: string }
>(({ scrollable = true, className, padding = 'sm none', ...props }, ref) => {
  return (
    <Box
      ref={ref}
      flex="1 1 auto"
      padding={padding}
      className={cx('core-menu__options', className, {
        'core-menu__options--scrollable': scrollable,
      })}
      {...props}
    />
  )
})

export const Options = React.forwardRef<HTMLDivElement, OptionsProps>(
  ({ children, className, scrollable = true, ...props }, ref) => {
    const menu = consumeMenu()

    let optionIndex = 0

    React.useEffect(() => {
      menu.setSize(
        React.Children.toArray(children).filter(menu.isSelectable).length
      )
    }, [children])

    return (
      <CSSMenuOptions
        ref={ref}
        {...props}
        scrollable={menu.scrollable || scrollable}
      >
        {React.Children.map(children, child =>
          menu.isSelectable(child) && menu.index === optionIndex++
            ? React.cloneElement(child as React.ReactElement<any>, {
                hover: true,
                ref: menu.hoverRef,
              })
            : child
        )}
      </CSSMenuOptions>
    )
  }
)

export const Search = React.forwardRef<
  HTMLInputElement,
  InputAttributes & SearchProps & React.InputHTMLAttributes<{}>
>(({ className, ...props }, ref) => (
  <Box className={cx('core-menu__search', className)} padding="lg lg sm">
    <SearchView
      autoFocus
      typeahead={true}
      placeholder="Search"
      ref={ref}
      {...props}
    />
  </Box>
))

export const Footer = React.forwardRef<
  HTMLDivElement,
  DivAttributes & FooterProps
>(({ className, padding = 'md lg', ...props }, ref) => (
  <Box
    ref={ref}
    className={cx('core-menu__footer', className)}
    padding={padding}
    {...props}
  />
))

export const MenuContext = React.createContext<MenuApi>({
  hoverRef: React.createRef(),
  index: -1,
  isSelectable: isItem,
  onSelect: () => {},
  scrollable: true,
  setSize: () => 0,
})

export const MenuConsumer = MenuContext.Consumer

export const consumeMenu = () => React.useContext(MenuContext)

Menu.displayName = 'Menu'

CSSMenuOptions.displayName = 'Menu.CSSMenuOptions'

Footer.displayName = 'Menu.Footer'

Group.displayName = 'Menu.Group'

Item.displayName = 'Menu.Item'

Options.displayName = 'Menu.Options'

Search.displayName = 'Menu.Search'

export default addSubcomponents(
  {
    CSSMenuOptions,
    Footer,
    Group,
    Item,
    Options,
    Search,
  },
  Menu
)
